import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import { ReactElement } from 'react'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CardActions from '@mui/material/CardActions'
import Button from '@mui/material/Button'
import Avatar from '@mui/material/Avatar'
import {
  ClassIcon,
  CourseIcon,
  NextIcon,
  QuizIcon,
  SectionIcon,
  StoryboardIcon,
  TextContentIcon,
  TitleScreenIcon,
  VideoIcon,
} from 'src/v3/core/icons'

import theme from 'src/theme'
import { useNavigate } from 'react-router-dom'
import { compose, always } from 'ramda'
import { useAppSelector } from 'src/store/hooks'
import { Breadcrumbs } from '@mui/material'
import {
  SelectLinkedService,
  SelectSection,
  SelectCourse,
  SelectClass,
} from 'src/v3/create/components/select-entities'
import { selectActiveClass } from './create.slice'

export default (): ReactElement => {
  let navigate = useNavigate()

  let activeClass = useAppSelector(selectActiveClass)

  return (
    <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
      <Typography variant="h6" className="my-4">
        Begin by creating a section and course.
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: 2,
          alignItems: 'start',
        }}
      >
        <Card variant="outlined">
          <CardContent>
            <div className="flex items-center mb-2">
              <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                <SectionIcon />
              </Avatar>
              <Typography className="pl-2" variant="h5" component="div">
                Sections
              </Typography>
            </div>
            <Typography sx={{ mb: 1.5 }} color="text.secondary">
              Categories that classes fit into
            </Typography>
            <Typography variant="body2">
              Before creating a class, you'll need to create a section.
            </Typography>
          </CardContent>
          <CardActions>
            <Button
              size="small"
              variant="outlined"
              onClick={
                compose(navigate, always('/create/section') as any) as any
              }
            >
              Manage Sections
            </Button>
          </CardActions>
        </Card>
        <Card variant="outlined">
          <CardContent>
            <div className="flex items-center mb-2">
              <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                <CourseIcon />
              </Avatar>
              <Typography className="pl-2" variant="h5" component="div">
                Courses
              </Typography>
            </div>
            <Typography sx={{ mb: 1.5 }} color="text.secondary">
              A class or series of classes
            </Typography>
            <Typography variant="body2">
              Name your course and give it a thumbnail. Then you can begin
              addings classes to it.
            </Typography>
          </CardContent>
          <CardActions>
            <Button
              variant="outlined"
              size="small"
              onClick={
                compose(navigate, always('/create/course') as any) as any
              }
            >
              Manage Courses
            </Button>
          </CardActions>
        </Card>
        <Card variant="outlined">
          <CardContent>
            <div className="flex items-center mb-2">
              <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                <ClassIcon />
              </Avatar>
              <Typography className="pl-2" variant="h5" component="div">
                Classes
              </Typography>
            </div>
            <Typography sx={{ mb: 1.5 }} color="text.secondary">
              All of your content, including videos, text, and a quiz
            </Typography>
            <Typography variant="body2">
              Name your class and give it a thumbnail. Then you can begin
              uploading content for it.
            </Typography>
          </CardContent>
          <CardActions>
            <Button
              variant="outlined"
              size="small"
              onClick={compose(navigate, always('/create/class') as any) as any}
            >
              Manage Classes
            </Button>
          </CardActions>
        </Card>
      </Box>
      <Divider className="my-4" />
      <Typography variant="h6" className="mt-4">
        Select a class and then begin creating content for it.
      </Typography>
      <Breadcrumbs
        sx={{ pt: 1, pb: 2 }}
        aria-label="breadcrumb"
        separator={<NextIcon fontSize="small" />}
      >
        <div style={{ margin: '8px 0' }}>
          <SelectLinkedService />
        </div>
        <div style={{ margin: '8px 0' }}>
          <SelectSection />
        </div>
        <div style={{ margin: '8px 0' }}>
          <SelectCourse />
        </div>
        <div style={{ margin: '8px 0' }}>
          <SelectClass />
        </div>
      </Breadcrumbs>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: 2,
          alignItems: 'start',
        }}
      >
        <Card variant="outlined">
          <CardContent>
            <div className="flex items-center mb-2">
              <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                <TitleScreenIcon />
              </Avatar>
              <Typography className="pl-2" variant="h5" component="div">
                Title Screen
              </Typography>
            </div>
            <Typography sx={{ mb: 1.5 }} color="text.secondary">
              Introductory information
            </Typography>
            <Typography variant="body2">
              This is your opportunity to allow the student to read about the
              class, and to provide a fancy hero image.
            </Typography>
          </CardContent>
          <CardActions>
            <Button
              onClick={
                compose(navigate, always('/create/title-screen') as any) as any
              }
              disabled={activeClass === 'unselected'}
              variant="outlined"
              size="small"
            >
              Manage Title Screen
            </Button>
          </CardActions>
        </Card>
        <Card variant="outlined">
          <CardContent>
            <div className="flex items-center mb-2">
              <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                <VideoIcon />
              </Avatar>
              <Typography className="pl-2" variant="h5" component="div">
                Videos
              </Typography>
            </div>
            <Typography sx={{ mb: 1.5 }} color="text.secondary">
              The meat of a class
            </Typography>
            <Typography variant="body2">
              Upload videos of your instructional content. Each video must be
              completed by a student before moving on.
            </Typography>
          </CardContent>
          <CardActions>
            <Button
              onClick={() => navigate('/create/videos')}
              disabled={activeClass === 'unselected'}
              variant="outlined"
              size="small"
            >
              Manage Videos
            </Button>
          </CardActions>
        </Card>
        <Card variant="outlined">
          <CardContent>
            <div className="flex items-center mb-2">
              <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                <TextContentIcon />
              </Avatar>
              <Typography className="pl-2" variant="h5" component="div">
                Text Content
              </Typography>
            </div>
            <Typography sx={{ mb: 1.5 }} color="text.secondary">
              Written material to supplement videos
            </Typography>
            <Typography variant="body2">
              Add in some additional information, links to references, or images
              between videos for some variety.
            </Typography>
          </CardContent>
          <CardActions>
            <Button
              onClick={() => navigate('/create/text-content')}
              disabled={activeClass === 'unselected'}
              variant="outlined"
              size="small"
            >
              Manage Text Content
            </Button>
          </CardActions>
        </Card>
        <Card variant="outlined">
          <CardContent>
            <div className="flex items-center mb-2">
              <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                <QuizIcon />
              </Avatar>
              <Typography className="pl-2" variant="h5" component="div">
                Quiz
              </Typography>
            </div>
            <Typography sx={{ mb: 1.5 }} color="text.secondary">
              Multiple choice questions
            </Typography>
            <Typography variant="body2">
              The final step of each class is a quiz. You create the questions,
              choices, and provide explanations.
            </Typography>
          </CardContent>
          <CardActions>
            <Button
              onClick={() => navigate('/create/quiz')}
              disabled={activeClass === 'unselected'}
              variant="outlined"
              size="small"
            >
              Build Quiz
            </Button>
          </CardActions>
        </Card>
        <Card variant="outlined">
          <CardContent>
            <div className="flex items-center mb-2">
              <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                <StoryboardIcon />
              </Avatar>
              <Typography className="pl-2" variant="h5" component="div">
                Storyboard
              </Typography>
            </div>
            <Typography sx={{ mb: 1.5 }} color="text.secondary">
              Arrange all of the content
            </Typography>
            <Typography variant="body2">
              Arrange the title screen, videos, text content, and quiz into the
              final product.
            </Typography>
          </CardContent>
          <CardActions>
            <Button
              onClick={() => navigate('/create/storyboard')}
              disabled={activeClass === 'unselected'}
              variant="outlined"
              size="small"
            >
              Arrange Storyboard
            </Button>
          </CardActions>
        </Card>
      </Box>
    </Box>
  )
}
